// @flow

import React from "react";
import {
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Snackbar,
    Alert,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
} from "@mui/material";

import axios from "axios";

import { IconButton, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import {
    useAppContext,
    fmtQuantity,
    fmtMoney,
    fmtI18n,
} from "../../context/AppContext";

import { useLanguage } from "../../i18n";

import Image from "mui-image";

import { TENANT } from "../../constants";

type Props = {
    stepNo: number,
    activeStep: number,
    handleNext: () => void,
};

const ProductsToReturnStep = (props: Props): any => {
    const { state, setState } = useAppContext();
    const { t } = useLanguage();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [snackbarMessage, setSnackbarMessage] = React.useState(null);
    const handleShowError = (error: string) => {
        setSnackbarMessage(error);
    };
    const handleSnackbarClose = (_, reason) => {
        // Prevent closing on clickaway (optional)
        if (reason === "clickaway") return;
        setSnackbarMessage(null);
    };

    const handleDecrement = async (index) => {
        const line = state.returnRequestLines[index];
        const url = line._links.decrementQuantityToRemove.href;
        try {
            const response = await axios.post(
                url,
                {},
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    },
                },
            );

            const updatedLines = state.returnRequestLines.map((l) => {
                if (l.id === response.data.id) {
                    return {
                        ...l,
                        quantityToReturn: response.data.quantityToReturn,
                    };
                }
                return l;
            });
            setState({ ...state, returnRequestLines: updatedLines });
        } catch (error) {
            console.error("Error decrementing quantity:", error);

            const errorData = error.response?.data;
            if (errorData?.causes && Array.isArray(errorData.causes)) {
                const messages = errorData.causes
                    .map((cause) => cause.message)
                    .join("; ");
                handleShowError(messages);
            } else {
                handleShowError(
                    errorData?.message || t('errorOccurred'),
                );
            }
        }
    };

    const handleIncrement = async (index) => {
        const line = state.returnRequestLines[index];
        const url = line._links.incrementQuantityToRemove.href;
        try {
            const response = await axios.post(
                url,
                {},
                {
                    headers: {
                        "X-Tenant": TENANT,
                        "X-Sale-Order-No": state.saleOrderNo,
                        "X-Phone-Last4": state.phoneLast4,
                    },
                },
            );

            const updatedLines = state.returnRequestLines.map((l) => {
                if (l.id === response.data.id) {
                    return {
                        ...l,
                        quantityToReturn: response.data.quantityToReturn,
                    };
                }
                return l;
            });
            setState({ ...state, returnRequestLines: updatedLines });
        } catch (error) {
            console.error("Error incrementing quantity:", error);
            const errorData = error.response?.data;
            if (errorData?.causes && Array.isArray(errorData.causes)) {
                const messages = errorData.causes
                    .map((cause) => cause.message)
                    .join("; ");
                handleShowError(messages);
            } else {
                handleShowError(
                    errorData?.message || t('errorOccurred'),
                );
            }
        }
    };

    const isNew = state.returnRequest?.state === 'NEW';

    return (
        <Stack>
            <Typography variant="caption">
                {t('productsDescription')}
            </Typography>

            <Box>
                <Snackbar
                    open={snackbarMessage != null}
                    autoHideDuration={4000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity="error"
                        variant="filled"
                        sx={{ width: "100%" }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>

                {isMobile ? (
                    // Mobile card-based layout
                    <Stack spacing={2}>
                        {state.returnRequestLines?.map((line, index) => {
                            const imageUrl = `https://trix.tendenz.bg/rp/${line.stock.sku.substring(0, line.stock.sku.length - 2)}_1.jpg`;

                            return (
                                <Card key={index} variant="outlined">
                                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                        <Stack direction="row" spacing={2}>
                                            {/* Product Image */}
                                            <Box sx={{ flex: "0 0 auto", width: 80 }}>
                                                <Image
                                                    src={imageUrl}
                                                    style={{
                                                        cursor: "pointer",
                                                        borderRadius: 8,
                                                    }}
                                                    showLoading
                                                />
                                            </Box>

                                            {/* Product Info */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="subtitle2"
                                                    display="block"
                                                    noWrap
                                                >
                                                    {line.stock.sku}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: "vertical",
                                                    }}
                                                >
                                                    {fmtI18n(line.stock.name)}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    display="block"
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {fmtQuantity(line.quantity)} x {fmtMoney(line.unitPrice)}
                                                </Typography>
                                            </Box>
                                        </Stack>

                                        {/* Quantity Controls */}
                                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                {t('qtyToReturn')}:
                                            </Typography>
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                            >
                                                <IconButton
                                                    size="medium"
                                                    disabled={!isNew}
                                                    onClick={() => handleDecrement(index)}
                                                    sx={{
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        width: 36,
                                                        height: 36,
                                                    }}
                                                >
                                                    <RemoveIcon fontSize="small" />
                                                </IconButton>

                                                <Typography
                                                    sx={{
                                                        minWidth: 32,
                                                        textAlign: "center",
                                                        fontWeight: 600,
                                                        fontSize: 16,
                                                    }}
                                                >
                                                    {fmtQuantity(line.quantityToReturn)}
                                                </Typography>

                                                <IconButton
                                                    size="medium"
                                                    disabled={!isNew}
                                                    onClick={() => handleIncrement(index)}
                                                    sx={{
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        width: 36,
                                                        height: 36,
                                                    }}
                                                >
                                                    <AddIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Stack>
                ) : (
                    // Desktop table layout
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            width: "100px",
                                            minWidth: "100px",
                                        }}
                                    ></TableCell>
                                    <TableCell>
                                        {t('stockOrderedQtyAmount')}
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            width: "200px",
                                            minWidth: "200px",
                                        }}
                                    >
                                        {t('qtyToReturn')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {state.returnRequestLines?.map((line, index) => {
                                    const imageUrl = `https://trix.tendenz.bg/rp/${line.stock.sku.substring(0, line.stock.sku.length - 2)}_1.jpg`;

                                    return (
                                        <TableRow key={index}>
                                            <TableCell
                                                sx={{
                                                    width: "100px",
                                                    minWidth: "100px",
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        flex: "0 0 auto",
                                                        width: 100,
                                                    }}
                                                >
                                                    <Image
                                                        src={imageUrl}
                                                        style={{
                                                            cursor: "pointer",
                                                            borderRadius: 8,
                                                        }}
                                                        showLoading
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box
                                                    sx={{
                                                        flex: "1 1 100px",
                                                        minWidth: "100px",
                                                        wordBreak: "break-word",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="subtitle2"
                                                        display="block"
                                                    >
                                                        {line.stock.sku}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                    >
                                                        {fmtI18n(line.stock.name)}
                                                    </Typography>

                                                    <Box
                                                        sx={{
                                                            marginTop: 2,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                            size=""
                                                        >
                                                            {fmtQuantity(
                                                                line.quantity,
                                                            )}{" "}
                                                            x{" "}
                                                            {fmtMoney(
                                                                line.unitPrice,
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                sx={{
                                                    width: "200px",
                                                    minWidth: "200px",
                                                }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    alignItems="center"
                                                    justifyContent="middle"
                                                >
                                                    <IconButton
                                                        size="large"
                                                        disabled={!isNew}
                                                        onClick={() =>
                                                            handleDecrement(index)
                                                        }
                                                        sx={{
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            width: 40,
                                                            height: 40,
                                                        }}
                                                    >
                                                        <RemoveIcon />
                                                    </IconButton>

                                                    <Typography
                                                        sx={{
                                                            minWidth: 32,
                                                            textAlign: "center",
                                                            fontWeight: 600,
                                                            fontSize: 16,
                                                        }}
                                                    >
                                                        {fmtQuantity(
                                                            line.quantityToReturn,
                                                        )}
                                                    </Typography>

                                                    <IconButton
                                                        size="large"
                                                        disabled={!isNew}
                                                        onClick={() =>
                                                            handleIncrement(index)
                                                        }
                                                        sx={{
                                                            border: "1px solid",
                                                            borderColor: "divider",
                                                            width: 40,
                                                            height: 40,
                                                        }}
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>

            {props.activeStep === props.stepNo ? (
                <Button
                    variant="contained"
                    onClick={props.handleNext}
                    sx={{ mt: 1, mr: 1 }}
                >
                    {t('continue')}
                </Button>
            ) : null}
        </Stack>
    );
};

export default ProductsToReturnStep;
